import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Coins, Play, FileDown, Send } from "lucide-react";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";
import { escapeHtml as esc } from "../../lib/safeHtml";
import { localName, localFullName } from "../../lib/localName";

function thisMonth() { return new Date().toISOString().slice(0, 7); }

export default function PayrollPage() {
  const {  t , lang } = useI18n();
  const [period, setPeriod] = useState(thisMonth());
  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);

  const load = () => api.get(`/payroll?period=${period}`).then(r => setItems(r.data || []));
  useEffect(() => { load(); api.get("/employees").then(r => setEmps(r.data || [])); }, [period]);

  const run = async () => {
    try { const r = await api.post(`/payroll/run?period=${period}`); toast.success(`+${r.data.created}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const disburse = async () => {
    if (!window.confirm(`${t("disburse_confirm") || "Mark all payslips for"} ${period} ${t("as_paid_and_notify") || "as paid and notify employees?"}`)) return;
    try { const r = await api.post(`/payroll/disburse?period=${period}`); toast.success(`${r.data.items_paid} ${t("payslip")} · ${r.data.notifications_queued} ${t("notifications") || "notifications"}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const disburseOne = async (p) => {
    try { await api.post(`/payroll/disburse?payslip_id=${p.id}`); toast.success(t("success")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const downloadPayslip = (item) => {
    const emp = emps.find(e => e.id === item.employee_id);
    const div = document.createElement("div");
    div.style.padding = "32px";
    div.style.fontFamily = "Tajawal, sans-serif";
    div.style.direction = "rtl";
    // XSS-safe: every user/DB-supplied value is HTML-escaped before interpolation.
    div.innerHTML = `
      <h1 style="margin:0">${esc(t("payslip"))} — ${esc(item.period)}</h1>
      <hr/>
      <p><b>${esc(t("employee"))}:</b> ${esc(localFullName(emp, lang) || item.employee_id)}</p>
      <p><b>${esc(t("position"))}:</b> ${esc(emp?.position || "")} · ${esc(t("department"))}: ${esc(emp?.department || "")}</p>
      <table style="width:100%;border-collapse:collapse;margin-top:16px">
        <tr><td style="padding:6px;border:1px solid #ccc">${esc(t("base_salary"))}</td><td style="padding:6px;border:1px solid #ccc">${esc(item.base_salary)} ${esc(t("sar_unit"))}</td></tr>
        ${Object.entries(item.allowances || {}).map(([k, v]) => `<tr><td style="padding:6px;border:1px solid #ccc">${esc(t("allowances"))}: ${esc(k)}</td><td style="padding:6px;border:1px solid #ccc">+${esc(v)} ${esc(t("sar_unit"))}</td></tr>`).join("")}
        ${Object.entries(item.deductions || {}).map(([k, v]) => `<tr><td style="padding:6px;border:1px solid #ccc">${esc(t("deductions"))}: ${esc(k)}</td><td style="padding:6px;border:1px solid #ccc">-${esc(v)} ${esc(t("sar_unit"))}</td></tr>`).join("")}
        <tr><td style="padding:6px;border:1px solid #ccc"><b>${esc(t("gross"))}</b></td><td style="padding:6px;border:1px solid #ccc">${esc(item.gross)} ${esc(t("sar_unit"))}</td></tr>
        <tr><td style="padding:6px;border:1px solid #ccc;background:#f0fdf4"><b>${esc(t("net"))}</b></td><td style="padding:6px;border:1px solid #ccc;background:#f0fdf4"><b>${esc(item.net)} ${esc(t("sar_unit"))}</b></td></tr>
      </table>
      <div style="margin-top:48px;display:flex;justify-content:space-between">
        <div>__________________<br/>${esc(t("hr") || "HR")}</div>
        <div>__________________<br/>${esc(t("employee"))}</div>
      </div>`;
    html2pdf().from(div).set({ filename: `payslip-${item.period}-${emp?.full_name || item.employee_id}.pdf` }).save();
  };

  const total = items.reduce((s, i) => s + (i.net || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-semibold flex items-center gap-3"><Coins className="w-7 h-7 text-primary" /> {t("payroll")}</h1>
        <div className="flex gap-2 items-center">
          <Input type="month" value={period} onChange={e => setPeriod(e.target.value)} className="w-44" data-testid="payroll-period" />
          <Button onClick={run} data-testid="run-payroll-btn"><Play className="w-4 h-4 me-2" />{t("run_payroll")}</Button>
          {items.some(i => i.status !== "paid") && (
            <Button variant="default" onClick={disburse} data-testid="disburse-payroll-btn" className="bg-emerald-600 hover:bg-emerald-700"><Send className="w-4 h-4 me-2" />{t("disburse_payroll") || "Disburse"}</Button>
          )}
        </div>
      </div>

      <Card className="p-4 border shadow-none">
        <div className="text-sm flex justify-between items-center flex-wrap gap-2">
          <span>{items.length} {t("payslip")} · {new Set(items.map(i => i.employee_id)).size} {t("employees")}</span>
          <span className="font-mono text-lg text-primary font-semibold">{t("net")}: {total.toLocaleString()} {t("sar_unit")}</span>
        </div>
      </Card>

      <Card className="border shadow-none">
        <ul className="divide-y divide-border">
          {items.map(p => {
            const emp = emps.find(e => e.id === p.employee_id);
            return (
              <li key={p.id} className="p-4 flex items-center justify-between gap-3 hover:bg-muted/30" data-testid={`payroll-row-${p.id}`}>
                <div className="flex-1">
                  <div className="font-medium">{localFullName(emp, lang) || p.employee_id}</div>
                  <div className="text-xs text-muted-foreground">{t("base_salary")}: {p.base_salary} · {t("gross")}: {p.gross} · {t("net")}: <b className="text-primary">{p.net}</b> {t("sar_unit")}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === "paid" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" : "bg-muted text-muted-foreground"}`}>{p.status === "paid" ? (t("paid") || "paid") : (t("draft") || "draft")}</span>
                {p.status !== "paid" && (
                  <Button size="sm" variant="ghost" onClick={() => disburseOne(p)} title={t("disburse_payroll") || "Disburse"} data-testid={`disburse-${p.id}`}><Send className="w-3.5 h-3.5" /></Button>
                )}
                <Button size="sm" variant="outline" onClick={() => downloadPayslip(p)} data-testid={`payslip-${p.id}`}><FileDown className="w-3.5 h-3.5 me-1" />PDF</Button>
              </li>
            );
          })}
          {items.length === 0 && <li className="p-12 text-center text-muted-foreground">{t("no_data")}</li>}
        </ul>
      </Card>
    </div>
  );
}
